import {
  basketQueryKeys,
  BuyableItemWithQuantity,
  SyncBasketDto,
  SyncBasketMutationResult,
  useGetUserBasket,
  useSyncBasketMutation,
} from "@ac-expo/api";
import { BuyableItemCreationType, GetUserBasket } from "@ac-expo/api-types";
import { getBuyableItemGroupKey, groupBuyableItemsByKey } from "@ac-expo/utils";
import { useDebouncedCallback } from "@mantine/hooks";
import { BasketType } from "@prisma/client";
import { MutateOptions, useQueryClient } from "@tanstack/react-query";
import { isEqual, uniq } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getGuestCartItems,
  guestCartEmitter,
  GuestCartStorageItem,
  setGuestCartItems,
} from "./guest-cart-storage";
import { BuyableItemWithPricing, useLocalCart } from "./useLocalCart";

export const ORDER_REDIRECT_KEY = "order_redirect";

const transformBasketToItems = (cart?: GetUserBasket): BuyableItemWithPricing[] => {
  if (!cart?.itemBreakdowns) return [];

  return cart.itemBreakdowns.map((itemBreakdown) => ({
    buyableItem: itemBreakdown.buyableItem,
    pricing: itemBreakdown.basePricing,
  }));
};

const extractBaseQuantities = (cart?: GetUserBasket): Record<string, number> => {
  if (!cart?.basketItem) return {};

  const quantityMap: Record<string, number> = {};

  cart.basketItem.forEach((basketItem) => {
    const key = getBuyableItemGroupKey(basketItem.buyableItem);
    quantityMap[key] = (quantityMap[key] || 0) + 1;
  });

  return quantityMap;
};

const extractGuestBaseQuantities = (items: GuestCartStorageItem[]): Record<string, number> => {
  const quantityMap: Record<string, number> = {};

  items.forEach((item) => {
    quantityMap[item.key] = (quantityMap[item.key] || 0) + item.quantity;
  });

  return quantityMap;
};

const areCartsQuantitiesSynced = (cart1: Record<string, number>, cart2: Record<string, number>) => {
  return isEqual(cart1, cart2);
};

export const useSyncedCart = ({
  basketType,
  isAuthenticated,
}: {
  basketType: BasketType;
  isAuthenticated: boolean;
}) => {
  const queryClient = useQueryClient();
  const [guestCartItems, setGuestCartItemsState] = useState<GuestCartStorageItem[]>(() =>
    isAuthenticated ? [] : getGuestCartItems(basketType),
  );

  const {
    data: cart,
    isLoading: isLoadingCart,
    refetch: refetchCart,
  } = useGetUserBasket(basketType);
  const { mutate, isPending: isMutating } = useSyncBasketMutation(basketType);

  // Single source of truth for reads AND writes. Guests live in localStorage until the
  // server cart takes over, which happens once the guest cart is empty while the server
  // one is not: the guest-checkout transition (`clearGuestCartItems` runs before
  // `setQueryData` seeds the basket) — without that fallback the footer flashes 0€.
  // A guest session never flips `isAuthenticated`, so a guest who comes back to a
  // synced cart must keep writing to the server cart it reads (issue #66).
  const useServerCart =
    isAuthenticated || (guestCartItems.length === 0 && (cart?.basketItem.length ?? 0) > 0);
  const items = useMemo(
    () => {
      if (useServerCart && cart) {
        return transformBasketToItems(cart);
      }
      return guestCartItems.map((item) => ({
        buyableItem: item.buyableItem,
        pricing: item.pricing,
      }));
    },
    [cart, guestCartItems, useServerCart],
  );
  const baseQuantities = useMemo(
    () => {
      if (useServerCart && cart) {
        return extractBaseQuantities(cart);
      }
      return extractGuestBaseQuantities(guestCartItems);
    },
    [cart, guestCartItems, useServerCart],
  );
  const appliedDiscountCodes = useMemo(() => cart?.appliedDiscountCodes ?? [], [cart]);

  const localCart = useLocalCart(items, baseQuantities, appliedDiscountCodes);
  const { totalQuantities, localQuantities, cartItems } = localCart;

  const writeGuestQuantity = useCallback(
    (buyableItemKey: string, getNextQuantity: (current: number) => number) => {
      // Read back from storage instead of state so that clicks fired within the same
      // render can't overwrite each other.
      const storedItems = getGuestCartItems(basketType);
      const storedItem = storedItems.find((item) => item.key === buyableItemKey);
      const nextQuantity = Math.max(0, getNextQuantity(storedItem?.quantity ?? 0));

      if (!storedItem) {
        const source = cartItems.find((item) => item.key === buyableItemKey);
        if (!source) return;

        setGuestCartItemsState(
          setGuestCartItems(basketType, [
            ...storedItems,
            {
              key: buyableItemKey,
              buyableItem: source.buyableItem,
              pricing: source.pricing,
              quantity: nextQuantity,
            },
          ]),
        );
        return;
      }

      setGuestCartItemsState(
        setGuestCartItems(
          basketType,
          storedItems.map((item) =>
            item.key === buyableItemKey ? { ...item, quantity: nextQuantity } : item,
          ),
        ),
      );
    },
    [basketType, cartItems],
  );

  const incrementGuestItem = useCallback(
    (buyableItemKey: string, quantity = 1) =>
      writeGuestQuantity(buyableItemKey, (current) => current + quantity),
    [writeGuestQuantity],
  );

  const decrementGuestItem = useCallback(
    (buyableItemKey: string, quantity = 1) =>
      writeGuestQuantity(buyableItemKey, (current) => current - quantity),
    [writeGuestQuantity],
  );

  const setGuestQuantity = useCallback(
    (buyableItemKey: string, quantity: number) =>
      writeGuestQuantity(buyableItemKey, () => quantity),
    [writeGuestQuantity],
  );

  const removeGuestItem = useCallback(
    (buyableItemKey: string) => writeGuestQuantity(buyableItemKey, () => 0),
    [writeGuestQuantity],
  );

  const baseCartHasError = useMemo(
    () => cart?.basketItem?.some((item) => item.error !== null) ?? false,
    [cart?.basketItem],
  );

  // Every read/write below follows the same source as `items`: a populated server
  // basket (guest placeholder included) is edited on the server, otherwise in localStorage
  const isSynced = useMemo(
    () => (useServerCart ? areCartsQuantitiesSynced(totalQuantities, baseQuantities) : true),
    [baseQuantities, useServerCart, totalQuantities],
  );

  const getErrors = useCallback(
    (buyableItemKey: string) => {
      const groupedItems = groupBuyableItemsByKey(cart?.basketItem ?? []);

      return uniq(
        (groupedItems[buyableItemKey] ?? [])
          .map((item) => item.error)
          .filter((error): error is NonNullable<typeof error> => error !== null),
      );
    },
    [cart?.basketItem],
  );

  const mutateCart = useCallback(
    (options?: MutateOptions<SyncBasketMutationResult, Error, SyncBasketDto, unknown>) => {
      // Guest quantities are already persisted on click, nothing to flush.
      if (!useServerCart || isSynced) {
        return;
      }

      const buyableItemsWithQuantity: BuyableItemWithQuantity[] = cartItems.map((item) => ({
        buyableItem: item.buyableItem as BuyableItemCreationType,
        quantity: item.quantity,
      }));

      mutate(
        {
          type: basketType,
          items: buyableItemsWithQuantity,
        },
        {
          ...options,
          onSuccess: (data, ...args) => {
            options?.onSuccess?.(data, ...args);
            queryClient.setQueryData(basketQueryKeys.get(basketType), () => data.basket);
            localCart.decrementItems(localQuantities);
          },
          onError: (...args) => {
            options?.onError?.(...args);
            localCart.decrementItems(localQuantities);
          },
        },
      );
    },
    [
      basketType,
      cartItems,
      useServerCart,
      isSynced,
      localCart,
      localQuantities,
      mutate,
      queryClient,
    ],
  );

  const setOrderRedirect = (path: string) => {
    localStorage.setItem(ORDER_REDIRECT_KEY, path);
  };

  const getOrderRedirect = () => {
    const path = localStorage.getItem(ORDER_REDIRECT_KEY);
    localStorage.removeItem(ORDER_REDIRECT_KEY);
    return path ?? "/";
  };

  const syncCart = useDebouncedCallback(mutateCart, 400);

  useEffect(() => {
    const isLocalQuantitiesChanged = Object.values(localQuantities).some((value) => value !== 0);

    if (isLocalQuantitiesChanged && !isMutating) {
      syncCart();
    }
  }, [localQuantities, syncCart, isSynced, isMutating]);

  const basketItemMap = useMemo(
    () => (cart ? new Map(cart.basketItem.map((item) => [item.id, item] as const)) : null),
    [cart?.basketItem],
  );

  const hasItems = useMemo(() => {
    if (useServerCart) {
      return cart?.basketItem && cart?.basketItem.length > 0;
    }
    return cartItems.some((item) => item.quantity > 0);
  }, [cart?.basketItem, cartItems, useServerCart]);

  useEffect(() => {
    if (isAuthenticated) return;
    setGuestCartItemsState(getGuestCartItems(basketType));
  }, [basketType, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) return;

    const handleGuestCartChange = () => {
      setGuestCartItemsState(getGuestCartItems(basketType));
    };

    guestCartEmitter.on("change", handleGuestCartChange);
    return () => {
      guestCartEmitter.off("change", handleGuestCartChange);
    };
  }, [basketType, isAuthenticated]);

  return {
    cart,
    basketItemMap,
    baseCartHasError,
    getErrors,
    isLoading: isLoadingCart || isMutating,
    isMutating,
    isSynced,
    hasItems,
    getOrderRedirect,
    setOrderRedirect,
    forceMutate: mutateCart,
    mutateCart,
    refetchCart,
    ...localCart,
    incrementItem: useServerCart ? localCart.incrementItem : incrementGuestItem,
    decrementItem: useServerCart ? localCart.decrementItem : decrementGuestItem,
    setQuantity: useServerCart ? localCart.setQuantity : setGuestQuantity,
    removeItem: useServerCart ? localCart.removeItem : removeGuestItem,
  };
};
